import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ExactConfig, PAYMENT_TYPES } from 'services/payment/payment.interface';
import hmacMD5 from 'crypto-js/hmac-md5';
import { Store } from '@ngrx/store';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { take, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { FeesStepInstance } from 'services/professional/step/step.interface';
import { PAYMENT_FORM_PLACE } from 'features/payment-form/payment-form.constants';
import { api_path, getHostName } from '../../../global';

interface ExactForm {
  transactionLogin: string;
  paymentAmount: number;
  fpSequence: string;
  currency: string;
  timestamp: string;
  hash: string;
  showForm: string;
  type: string;
  receiptLinkMethod: string;
  receiptLinkText: string;
  receiptLinkUrl: string;
  transactionId?: number;
  description: string;
}

@Component({
  selector: 'cm-exact-form',
  templateUrl: './exact-form.component.html',
  styleUrls: ['./exact-form.component.scss'],
  standalone: false,
})
export class ExactFormComponent implements OnInit {
  @Input() paymentConfig: ExactConfig;
  @Input() paymentAmount: number;
  @Input() step: FeesStepInstance;
  @Input() paymentPlace: PAYMENT_FORM_PLACE;
  @ViewChild('form') form;

  exactPaymentPath = environment.exactPaymentPath;
  exactForm: ExactForm;
  private currency: string;

  constructor(private store: Store<any>) {}

  ngOnInit() {
    this.loadForm(this.paymentConfig, this.paymentAmount);
  }

  private generateTimeHashParams() {
    const randNumber = Math.floor(100000 + Math.random() * 900000).toString();
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const hash = hmacMD5(
      `${this.paymentConfig.transactionLogin}^${randNumber}^${timestamp}^${this.paymentAmount}^${this.currency}`,
      this.paymentConfig.transactionKey,
    );
    return {
      randNumber,
      timestamp,
      hash,
    };
  }

  private updateFormWithNewTimeValues({ transactionId, description }: { transactionId: string; description: string }) {
    const { randNumber, timestamp, hash } = this.generateTimeHashParams();
    const { x_fp_sequence, x_fp_timestamp, x_fp_hash, x_invoice_num, x_description } = this.form.nativeElement.elements;
    x_fp_sequence.value = randNumber;
    x_fp_timestamp.value = timestamp;
    x_fp_hash.value = hash;
    x_description.value = description;
    if (transactionId) {
      x_invoice_num.value = transactionId;
    }
  }

  private loadForm(paymentConfig: ExactConfig, paymentAmount: number) {
    this.store
      .select(Selectors.paymentCurrency)
      .pipe(
        take(1),
        map((currency) => currency.toUpperCase()),
      )
      .subscribe((currency) => {
        this.currency = currency;
        const { randNumber, timestamp, hash } = this.generateTimeHashParams();

        this.exactForm = {
          transactionLogin: paymentConfig.transactionLogin,
          paymentAmount,
          fpSequence: randNumber,
          currency,
          timestamp,
          hash,
          showForm: 'PAYMENT_FORM',
          type: 'AUTH_CAPTURE',
          receiptLinkMethod: 'AUTO-POST',
          receiptLinkText: 'Return to Certemy',
          receiptLinkUrl: `${this.getApiPath()}/payment/exact/after-payment`,
          description: '',
        };
      });
  }

  private getApiPath() {
    if (environment.name === 'Local') {
      return api_path;
    }
    return `${window.location.protocol}//${getHostName()}/api`;
  }

  submitForm(values) {
    this.updateFormWithNewTimeValues(values);
    this.form.nativeElement.submit();
  }

  getData() {
    return {
      data: {
        pages: {
          current: window.location.origin + window.location.pathname,
          success: `${window.location.origin}/payment-checkout/success?redirectPath=${window.location.pathname}`,
          error: `${window.location.origin}/payment-checkout/failed?redirectPath=${window.location.pathname}`,
        },
        step_instance_id: this.step.step_instance_id,
        amount: this.paymentAmount,
        payment_place: this.paymentPlace,
      },
      type: PAYMENT_TYPES.EXACT,
      callback: ({ type, ...values }: { type: PAYMENT_TYPES }) => {
        if (type !== PAYMENT_TYPES.EXACT) {
          return;
        }
        this.submitForm(values);
      },
    };
  }
}
